import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { LabsHeaderContainer } from './labs-header-container';
import { BiomarkerCard } from '../biomarker-card';
import { CategoryTagScroller } from '../category-tag-scroller';
import { useDragScroll } from '../../hooks/use-drag-scroll';
import { useMemo } from 'react';
import type { CategoryWithResults, LabOrderListItem } from '@/api/generated-catch-backend/model';
import { useGetBiomarkersV1BiomarkersGet } from '@/api/generated-catch-backend/biomarkers/biomarkers';
import { ROUTE_LABS_ALL } from '@/constants/routes';

interface LabsHeaderBiomarkersProps {
  className?: string;
  latestOrder?: LabOrderListItem;
  onCategoryClick?: (categorySlug: string) => void;
}

export function LabsHeaderBiomarkers({ className, latestOrder, onCategoryClick }: LabsHeaderBiomarkersProps) {
  const { scrollRef, isDragging, handlers } = useDragScroll<HTMLDivElement>();

  // Fetch biomarker counts from API
  const { data: biomarkersData, isLoading: isBiomarkersLoading } = useGetBiomarkersV1BiomarkersGet();

  // Calculate biomarker counts
  const biomarkerCounts = useMemo(() => {
    if (!biomarkersData?.summary) {
      return { optimal: 0, normal: 0, outOfRange: 0 };
    }

    return {
      optimal: biomarkersData.summary.optimal ?? 0,
      normal: biomarkersData.summary.normal ?? 0,
      // Combine out_of_range and critical counts
      outOfRange: (biomarkersData.summary.outOfRange ?? 0) + (biomarkersData.summary.critical ?? 0),
    };
  }, [biomarkersData]);

  // Calculate the dates for display
  const { lastUpdatedDate, nextLabDate } = useMemo(() => {
    if (!latestOrder) {
      return { lastUpdatedDate: 'N/A', nextLabDate: 'N/A' };
    }

    // V1 API uses createdAtUtc for order creation date
    const dateStr = latestOrder.orderedAtUtc || latestOrder.createdAtUtc;
    const date = new Date(dateStr);

    // Format: "Dec 2025"
    const lastUpdated = date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });

    // Calculate next lab date (+ 1 year)
    const nextLabDateObj = new Date(date);
    nextLabDateObj.setFullYear(nextLabDateObj.getFullYear() + 1);
    const nextLab = nextLabDateObj.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });

    return {
      lastUpdatedDate: lastUpdated,
      nextLabDate: nextLab,
    };
  }, [latestOrder]);

  // Map categories from API data
  const categories = useMemo(() => {
    if (!biomarkersData?.categories) return [];

    return biomarkersData.categories.map((category: CategoryWithResults, index: number) => ({
      id: category.metadata.slug,
      label: category.metadata.displayName,
      isActive: index === 0, // First category is active by default
    }));
  }, [biomarkersData]);

  return (
    <LabsHeaderContainer className={className}>
      {/* Date tabs */}
      <div className="mb-4 flex flex-row items-center justify-center gap-2 md:mb-6 md:justify-start md:gap-4">
        <div className="whitespace-nowrap rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold leading-tight text-white backdrop-blur-sm">
          Last Updated {lastUpdatedDate}
        </div>
        <div className="whitespace-nowrap rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold leading-tight text-white backdrop-blur-sm">
          Next Lab {nextLabDate}
        </div>
      </div>

      {/* Title */}
      <motion.h1
        className="mb-6 text-center text-2xl font-bold leading-tight text-white md:mb-8 md:text-left md:text-[44px] md:font-normal md:leading-[56px] md:tracking-[0.3px]"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        Your Catch Biomarkers
      </motion.h1>

      {/* Biomarker Cards */}
      <div className="-mx-4 px-4 md:mx-0 md:px-0">
        <div
          ref={scrollRef}
          {...handlers}
          className={`flex justify-center gap-2 overflow-x-auto md:grid md:grid-cols-3 md:gap-6 md:overflow-visible [&::-webkit-scrollbar]:hidden ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab md:cursor-auto'
          }`}
        >
          {isBiomarkersLoading ? (
            <>
              {/* Skeleton loading state */}
              <div className="shrink-0 md:w-auto">
                <div className="h-[120px] w-[160px] animate-pulse rounded-lg bg-gray-700/50 md:w-full" />
              </div>
              <div className="shrink-0 md:w-auto">
                <div className="h-[120px] w-[160px] animate-pulse rounded-lg bg-gray-700/50 md:w-full" />
              </div>
              <div className="shrink-0 md:w-auto">
                <div className="h-[120px] w-[160px] animate-pulse rounded-lg bg-gray-700/50 md:w-full" />
              </div>
            </>
          ) : (
            <>
              <div className="shrink-0 md:w-auto">
                <BiomarkerCard
                  count={biomarkerCounts.optimal}
                  label="Optimal"
                  color="bg-catch-avocado-400"
                  onViewDetails={() => console.log('View Optimal biomarkers')}
                  alignLeft
                />
              </div>
              <div className="shrink-0 md:w-auto">
                <BiomarkerCard
                  count={biomarkerCounts.normal}
                  label="Normal"
                  color="bg-catch-light-blue"
                  onViewDetails={() => console.log('View Normal biomarkers')}
                  alignLeft
                />
              </div>
              <div className="shrink-0 md:w-auto">
                <BiomarkerCard
                  count={biomarkerCounts.outOfRange}
                  label="Out of Range"
                  color="bg-catch-salmon"
                  onViewDetails={() => console.log('View Out of Range biomarkers')}
                  alignLeft
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* View All Link */}
      <div className="mt-4 text-center md:mt-6 md:text-left">
        <Link
          to={ROUTE_LABS_ALL}
          className="text-catch-avocado-400 underline hover:text-catch-green-dark"
        >
          View all Biomarkers
        </Link>
      </div>

      {/* Category Tags */}
      <CategoryTagScroller tags={categories} onTagClick={onCategoryClick} />
    </LabsHeaderContainer>
  );
}
